import { unwrap } from "@sagot/api-client";
import {
  CONSOLE_PATHS,
  DEFAULT_ORG_SIZE,
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  INDUSTRIES,
  INDUSTRY_ICONS,
  ORG_SIZES,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PRODUCT_NAME,
  TURNSTILE_ACTIONS,
  type OrgSizeKey,
} from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Field, Icon, Input, Select } from "@sagot/ui";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { CountrySelect } from "../lib/region-pickers";
import { zodResolver } from "../lib/zod-resolver";
import { writePendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { AuthDivider } from "./components/auth-divider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { OAuthButton } from "./components/oauth-buttons";
import { PasswordInput } from "./components/password-input";
import { PasswordStrength } from "./components/password-strength";
import { PillChoice } from "./components/pill-choice";
import { Turnstile } from "./components/turnstile";
import { RegisterConsents } from "./register-consents";
import { WorkspaceUrlHint } from "./workspace-url-hint";

const SIZE_LABEL: Record<OrgSizeKey, string> = {
  lt_200: "sizeUnder200",
  "200_500": "size200to500",
  "501_1500": "size501to1500",
  "1501_5000": "size1501to5000",
  gt_5000: "sizeOver5000",
};

function useRegisterSchema() {
  const { t } = useTranslation();
  const accepted = z.literal(true, { errorMap: () => ({ message: t("auth.register.consentRequired") }) });
  return z.object({
    name: z.string().trim().min(1, t("auth.common.nameRequired")).max(NAME_MAX_LENGTH),
    email: z.string().trim().min(1, t("auth.common.emailRequired")).max(EMAIL_MAX_LENGTH).email(t("auth.common.emailInvalid")),
    password: z.string().min(PASSWORD_MIN_LENGTH, t("auth.password.tooShort")).max(PASSWORD_MAX_LENGTH),
    orgName: z.string().trim().min(1, t("auth.register.orgNameRequired")).max(NAME_MAX_LENGTH),
    orgType: z.enum(INDUSTRIES),
    size: z.enum(ORG_SIZES),
    country: z.string().length(2),
    terms: accepted,
    dpa: accepted,
    marketing: z.boolean(),
  });
}

export type RegisterValues = z.infer<ReturnType<typeof useRegisterSchema>>;

export function RegisterPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const form = useForm<RegisterValues>({
    resolver: zodResolver(useRegisterSchema()),
    defaultValues: { name: "", email: "", password: "", orgName: "", orgType: "business", size: DEFAULT_ORG_SIZE, country: "PH", marketing: false },
  });
  const { register, control, formState } = form;
  const [password, orgName] = useWatch({ control, name: ["password", "orgName"] });

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      await unwrap(
        api.POST("/v1/auth/register", {
          body: {
            name: v.name,
            email: v.email,
            password: v.password,
            org_name: v.orgName,
            org_type: v.orgType,
            size: v.size,
            country: v.country,
            consents: { terms: v.terms, dpa: v.dpa, marketing: v.marketing },
            captcha_token: captcha,
          },
        }),
      );
      writePendingAuth({ kind: "verify-email", email: v.email });
      await navigate(`${CONSOLE_PATHS.verifyEmail}?email=${encodeURIComponent(v.email)}`);
    } catch (caught) {
      setError(message(caught));
      setCaptchaReset((n) => n + 1);
    }
  });

  return (
    <AuthLayout
      title={t("auth.register.title", { product: PRODUCT_NAME })}
      prompt={{ text: t("auth.common.alreadyHaveAccount"), label: t("auth.common.signIn"), href: CONSOLE_PATHS.login }}
      width="md"
      align="start"
    >
      <div>
        <Badge tone="brand">
          <Icon name="sparkles" className="size-3" />
          {t("auth.register.trial")}
        </Badge>
        <h1 className="mt-3 text-2xl font-extrabold tracking-tight">{t("auth.register.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("auth.register.subtitle")}</p>
      </div>
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mt-5" /> : null}
      <form className="mt-6 space-y-5" noValidate onSubmit={(event) => void onSubmit(event)}>
        <section className="space-y-4" aria-labelledby="reg-about">
          <h2 id="reg-about" className="text-[11px] font-bold uppercase tracking-[0.14em] text-fg-subtle">
            {t("auth.register.aboutYou")}
          </h2>
          <Field label={t("auth.register.fullName")} error={formState.errors.name?.message}>
            <Input autoComplete="name" {...register("name")} />
          </Field>
          <Field label={t("auth.common.workEmail")} hint={t("auth.register.emailHint")} error={formState.errors.email?.message}>
            <Input type="email" autoComplete="email" inputMode="email" {...register("email")} />
          </Field>
          <Field label={t("auth.common.password")} error={formState.errors.password?.message}>
            <PasswordInput placeholder={t("auth.password.placeholder")} autoComplete="new-password" {...register("password")} />
            <PasswordStrength value={password} />
          </Field>
        </section>
        <div className="divider" />
        <section className="space-y-4" aria-labelledby="reg-org">
          <h2 id="reg-org" className="text-[11px] font-bold uppercase tracking-[0.14em] text-fg-subtle">
            {t("auth.register.yourOrganization")}
          </h2>
          <Field label={t("auth.register.orgName")} hint={<WorkspaceUrlHint name={orgName} />} error={formState.errors.orgName?.message}>
            <Input autoComplete="organization" {...register("orgName")} />
          </Field>
          <div>
            <span className="label" id="reg-type">
              {t("auth.register.orgType")}
            </span>
            <Controller
              control={control}
              name="orgType"
              render={({ field }) => (
                <PillChoice
                  aria-labelledby="reg-type"
                  value={field.value}
                  onChange={field.onChange}
                  options={INDUSTRIES.map((type) => ({ value: type, label: t(`onboarding.industries.${type}`), icon: INDUSTRY_ICONS[type] }))}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t("auth.register.size")}>
              <Select {...register("size")}>
                {ORG_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {t(`auth.register.${SIZE_LABEL[size]}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("auth.register.country")} id="reg-country">
              <Controller control={control} name="country" render={({ field }) => <CountrySelect id="reg-country" value={field.value} onChange={field.onChange} />} />
            </Field>
          </div>
        </section>
        <RegisterConsents form={form} />
        <Turnstile action={TURNSTILE_ACTIONS.register} onToken={setCaptcha} resetKey={captchaReset} />
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting} disabled={formState.isSubmitting || !captcha}>
          {t("auth.register.submit")}
          <Icon name="arrow-right" />
        </Button>
      </form>
      <AuthDivider label={t("auth.register.orSignUpWith")} />
      <div className="grid grid-cols-2 gap-2.5">
        <OAuthButton provider="google" intent="signup" label={t("auth.common.google")} />
        <OAuthButton provider="microsoft" intent="signup" label={t("auth.common.microsoft")} />
      </div>
      <p className="mt-8 text-center text-sm text-fg-muted">
        {t("auth.common.alreadyHaveAccount")}{" "}
        <AppLink href={CONSOLE_PATHS.login} className="font-semibold text-primary-ink hover:underline">
          {t("auth.common.signIn")}
        </AppLink>
      </p>
    </AuthLayout>
  );
}
